import { ref, reactive, watch } from 'vue'
import * as yup from 'yup'

type ValidationSchema = Record<string, yup.Schema>

export function useFormWithValidation<T extends Record<string, any>>(
  initialData: T,
  initialSchema: ValidationSchema
) {
  const formData = reactive<T>({ ...initialData })
  const formErrors = reactive<Record<string, string>>({})
  const touchedFields = reactive<Record<string, boolean>>({})
  const schema = ref<ValidationSchema>(initialSchema)

  const validate = async () => {
    const validationSchema = yup.object().shape(schema.value)

    try {
      await validationSchema.validate(formData, { abortEarly: false })
      return { valid: true, errors: {} }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const errors = err.inner.reduce(
          (acc, error) => {
            if (error.path) {
              acc[error.path] = error.message
            }
            return acc
          },
          {} as Record<string, string>
        )
        return { valid: false, errors }
      }
      return { valid: false, errors: { general: 'Validation failed' } }
    }
  }

  const validateField = async (fieldName: string, value: any) => {
    if (!schema.value[fieldName]) return true

    try {
      await schema.value[fieldName].validate(value)
      formErrors[fieldName] = ''
      return true
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        formErrors[fieldName] = error.message
      }
      return false
    }
  }

  const handleFieldTouch = (fieldName: string) => {
    touchedFields[fieldName] = true

    if (formErrors.value) {
      formErrors.value[fieldName] = ''
    }
  }

  const handleFieldChange = async (fieldName: string, value: any) => {
    formData[fieldName as keyof T] = value
    touchedFields[fieldName] = true
    await validateField(fieldName, value)
  }

  const updateSchema = (newSchema: ValidationSchema) => {
    schema.value = newSchema
  }

  const showErrors = (errors: any) => {
    formErrors.value = errors
  }

  return {
    formData,
    formErrors,
    touchedFields,
    validate,
    validateField,
    handleFieldTouch,
    handleFieldChange,
    updateSchema,
    showErrors
  }
}
